'use client';

import { useState, useMemo } from 'react';
import { useAuthStore } from '@/stores/auth';
import { useRouter } from '@/i18n/routing';
import { Shield, X } from 'lucide-react';

const STORAGE_KEY = 'two_factor_banner_dismissed_session';

export function TwoFactorPromotionBanner() {
  const user = useAuthStore((state) => state.user);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const authLoading = useAuthStore((state) => state.authLoading);
  const router = useRouter();
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem(STORAGE_KEY) === 'true';
    }
    return false;
  });

  // Compute visibility based on auth state
  const show = useMemo(() => {
    // Only check after auth has loaded
    if (authLoading) return false;

    // Don't show if user is not logged in
    if (!isLoggedIn || !user) return false;

    // Only show for email/password users (not OAuth users)
    if (user.provider !== 'email_password') return false;

    // Don't show if 2FA is already enabled
    if (user.is_two_factor_enabled) return false;

    // Don't show if dismissed
    if (dismissed) return false;

    return true;
  }, [authLoading, isLoggedIn, user, dismissed]);

  const handleEnable = () => {
    router.push('/settings');
  };

  const handleDismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, 'true');
    setDismissed(true);
  };

  if (!show) {
    return null;
  }

  return (
    <div className="w-full bg-[#E0F2FE] border-b border-[#BAE6FD]">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between gap-4 h-10 max-h-10">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Shield className="flex-shrink-0 text-[#0369A1]" size={16} />
            <p className="text-[#0369A1] text-[13px] leading-tight flex-1 min-w-0">
              Bezpieczeństwo na fali: Włącz weryfikację dwuetapową (2FA), aby lepiej chronić swój profil.{' '}
              <button
                onClick={handleEnable}
                className="font-bold underline hover:text-[#075985] transition-colors inline"
              >
                Włącz teraz
              </button>
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 hover:bg-[#BAE6FD] rounded transition-colors"
            aria-label="Zamknij"
          >
            <X className="text-[#0369A1]" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

